import { format } from 'date-fns';
import { studioDayKey } from '@atink/domain';

/** The studio-local (Melbourne) calendar day of an instant, as YYYY-MM-DD.
 * Robust to whether the ISO carries an offset or a UTC Z suffix (the sim clock advance
 * emits Z), follows daylight saving (AEST +10:00 / AEDT +11:00) and never depends on the
 * machine timezone. Idempotent for date-only inputs. One rule shared with the domain
 * package (`studioDayKey`) so web and api never disagree on a day (tv-2na.10 m1). */
export function dayKey(iso: string): string {
  return studioDayKey(iso);
}

/**
 * A host-local Date on the studio (Melbourne) calendar day of `iso` (noon, clear
 * of any daylight-saving edge). For date pickers and day labels that format a
 * Date with date-fns: `format(new Date(iso), …)` reads the HOST day, which is a
 * day out in a browser far from Melbourne (88v6).
 */
export function studioCalendarDate(iso: string): Date {
  const [y, m, d] = studioDayKey(iso).split('-').map(Number);
  return new Date(y!, m! - 1, d!, 12);
}

/** A day-level label (e.g. 'd MMM', 'EEE d MMM') for the studio day of `iso`, in any browser zone. */
export function formatStudioDay(iso: string, pattern: string): string {
  return format(studioCalendarDate(iso), pattern);
}

const STUDIO_TIME_FMT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Australia/Melbourne',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** `HH:mm` Melbourne wall time of an instant, in any browser zone. */
export function studioTime(iso: string): string {
  return STUDIO_TIME_FMT.format(new Date(iso));
}
